import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Popover,
  MenuList,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useFeature, Features } from '@billda/features-ui';
import {
  MailOutline as MailOutlineIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { UserStatus } from '../api/userManagementApi';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { useUsers } from '../hooks/useUsers';
import { useUserManagementUIStore } from '../stores/userManagementUIStore';
import { useUserPanelState } from '../hooks/useUserPanelState';
import { UsersListView } from '../components/organisms/UsersListView';
import InviteUserPanel from '../components/InviteUserPanel';
import PendingInvitesPanel from '../components/PendingInvitesPanel';
import { usePendingInvites } from '../hooks/usePendingInvites';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import { USERS_HELP } from '../../../shared/constants/helpContent';
import { useEscapeKey } from '../../../shared/hooks/useEscapeKey';
import { routeBuilders } from '../../../routes';

const OFFBOARDED_TAB_VALUE = '__offboarded__';

const UserManagementPage = () => {
  const navigate = useNavigate();

  // Use React Query for users list
  const { data: users = [], isLoading: loadingUsers } = useUsers();

  // Get pending invites count for badge
  const { data: pendingData } = usePendingInvites();
  const pendingCount = pendingData?.totalCount ?? 0;

  // Offboarding feature flag
  const isOffboardingEnabled = useFeature(Features.Offboarding.IsEnabled);
  const { showOffboarded, setShowOffboarded } = useUserManagementUIStore();

  // Role filter state
  const [roleFilter, setRoleFilter] = useState('all');

  // UI state from store
  const { searchText, setSearchText } = useUserManagementUIStore();

  // Panel state from URL
  const {
    isOpen: isPanelOpen,
    type: contentType,
    openPanel,
    closePanel,
  } = useUserPanelState();

  // ESC key to close panel
  useEscapeKey(closePanel, isPanelOpen);

  const loading = loadingUsers;

  // Actions menu state
  const [actionsMenuAnchorElement, setActionsMenuAnchorElement] =
    useState<HTMLElement | null>(null);

  // Derive unique roles from users for ChipTabs
  const roleTabs = useMemo(() => {
    const roles = new Set<string>();
    users.forEach(user => user.roles?.forEach(role => roles.add(role)));
    const sorted = Array.from(roles).sort();

    const tabs = [{ label: 'All', value: 'all' }];

    if (isOffboardingEnabled && showOffboarded) {
      tabs.push({ label: 'Offboarded', value: OFFBOARDED_TAB_VALUE });
    }

    if (isOffboardingEnabled && sorted.includes('Offboarding')) {
      tabs.push({ label: 'Offboarding', value: 'Offboarding' });
      const remaining = sorted.filter(r => r !== 'Offboarding');
      tabs.push(...remaining.map(role => ({ label: role, value: role })));
    } else {
      tabs.push(...sorted.map(role => ({ label: role, value: role })));
    }

    return tabs;
  }, [users, isOffboardingEnabled, showOffboarded]);

  // Filter users based on search text and role
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Hide offboarded users by default when feature flag is on
    if (isOffboardingEnabled && !showOffboarded) {
      filtered = filtered.filter(user => user.status !== UserStatus.Inactive);
    }

    // Role filter
    if (roleFilter === OFFBOARDED_TAB_VALUE) {
      filtered = filtered.filter(user => user.status === UserStatus.Inactive);
    } else if (roleFilter !== 'all') {
      filtered = filtered.filter(user =>
        user.roles?.some(role => role === roleFilter)
      );
    }

    // Search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.fullName.toLowerCase().includes(searchLower) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.roles &&
            user.roles.some(role =>
              role.toLowerCase().includes(searchLower)
            )) ||
          ((user as any).username &&
            (user as any).username.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [users, searchText, roleFilter, isOffboardingEnabled, showOffboarded]);

  // Reset role filter when offboarded tab is hidden
  useEffect(() => {
    if (!showOffboarded && roleFilter === OFFBOARDED_TAB_VALUE) {
      setRoleFilter('all');
    }
  }, [showOffboarded, roleFilter]);

  const handleUserSelect = (userId: string) => {
    navigate(routeBuilders.users.detail(userId));
  };

  // Menu handlers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, _: string) => {
    setActionsMenuAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setActionsMenuAnchorElement(null);
  };

  const getPanelTitle = () => {
    if (contentType === 'invite') {
      return 'Invite User';
    }
    if (contentType === 'pending-invites') {
      return 'Pending Invites';
    }
    if (contentType === 'help') {
      return 'Help';
    }
    return 'Details';
  };

  return (
    <PageContentArea
      isPanelOpen={isPanelOpen}
      panel={
        isPanelOpen ? (
          <>
            <ResizableHandle />
            <SidePanel
              open={isPanelOpen}
              onClose={closePanel}
              title={getPanelTitle()}
              testId={
                contentType === 'invite'
                  ? 'invite-user-drawer'
                  : contentType === 'pending-invites'
                    ? 'pending-invites-drawer'
                    : contentType === 'help'
                      ? 'users-help-drawer'
                      : undefined
              }
            >
              {contentType === 'invite' && (
                <InviteUserPanel
                  onClose={closePanel}
                  onInviteSent={() => openPanel('pending-invites', 'list')}
                />
              )}
              {contentType === 'pending-invites' && <PendingInvitesPanel />}
              {contentType === 'help' && <HelpPanel helpContent={USERS_HELP} />}
            </SidePanel>
          </>
        ) : undefined
      }
    >
      <PageHeader
        title="Users"
        helpAction={{
          onClick: () => openPanel('help', 'users'),
          testId: 'users-help-button',
        }}
        actions={
          <>
            <PageHeaderAction
              label={`Invited${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
              onClick={() => openPanel('pending-invites', 'list')}
              icon={<MailOutlineIcon />}
              disabled={loading}
              data-testid="pending-invites-button"
            />
            <PageHeaderAction
              label="Add User"
              onClick={() => openPanel('invite', 'new')}
              icon={<PersonAddIcon />}
              disabled={loading}
              data-testid="invite-user-button"
            />
          </>
        }
        data-testid="users-header"
      />

      <PageToolbar
        tabs={
          <ChipTabs
            tabs={roleTabs}
            value={roleFilter}
            onChange={setRoleFilter}
            data-testid="users-role-tabs"
          />
        }
        searchValue={searchText}
        onSearchChange={setSearchText}
        filterContent={
          isOffboardingEnabled ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOffboarded}
                  onChange={(_, checked) => setShowOffboarded(checked)}
                  size="small"
                  data-testid="show-offboarded-checkbox"
                />
              }
              label="Show offboarded users"
              slotProps={{ typography: { variant: 'body2' } }}
              data-testid="show-offboarded-toggle"
            />
          ) : undefined
        }
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <UsersListView
          users={filteredUsers}
          loading={loading}
          onOpenMenu={handleOpenMenu}
          onItemClick={handleUserSelect}
        />
        {!loadingUsers && users.length > 0 && users.length < 10 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
            }}
          >
            <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400 }}>
              <PersonAddIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.primary">
                Grow your team
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Invite more workers so they can book work on your sites and get
                paid via the mobile app.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => openPanel('invite', 'new')}
                data-testid="users-cta-add-user"
              >
                Add User
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Actions Menu Popover */}
      <Popover
        open={Boolean(actionsMenuAnchorElement)}
        anchorEl={actionsMenuAnchorElement}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{
          marginTop: '8px',
          '& .MuiPaper-root': {
            minWidth: '150px',
          },
        }}
        data-testid="actions-popover"
      >
        <MenuList>
          <MenuItem onClick={handleCloseMenu} disabled>
            No actions available
          </MenuItem>
        </MenuList>
      </Popover>
    </PageContentArea>
  );
};

export default UserManagementPage;
